import { rtkq, RtkqRequest } from '../rtkq';
import { api } from '../api';
import {
  AddArticleDto,
  ArticleDto,
  ArticleLookupDto,
  articlesHttp,
  ArticleStatus,
  UpdateArticleDto,
} from '@/core/http.articles';
import { Paged, Paging } from '@/core';
import { SortResult } from '@/components';

export type ArticleState = ArticleDto;
export type ArticleLookupState = ArticleLookupDto;
export type AddArticleArgs = AddArticleDto;
export type UpdateArticleArgs = UpdateArticleDto;

const articlesApi = api.injectEndpoints({
  endpoints: (build) => ({
    getArticleList: build.query<ArticleState[], RtkqRequest<{}>>({
      queryFn: async (args) => {
        return rtkq(args).exec(() => articlesHttp.getAll());
      },
      providesTags: (result) => [
        { type: 'article', id: 'list' },
        ...(result || []).map(({ id }) => ({
          type: 'article' as const,
          id,
        })),
      ],
    }),

    getPublishedArticleList: build.query<
      ArticleState[],
      RtkqRequest<{ statuses?: ArticleStatus[] }>
    >({
      queryFn: async (args) => {
        return rtkq(args).exec(() => articlesHttp.getPublished(args.statuses));
      },
      providesTags: (result) => [
        { type: 'article', id: 'list' },
        ...(result || []).map(({ id }) => ({
          type: 'article' as const,
          id,
        })),
      ],
    }),

    searchArticles: build.query<
      Paged<ArticleLookupState>,
      RtkqRequest<{
        statuses?: ArticleStatus[];
        searchString?: string;
        paging: Paging;
        sorting?: SortResult;
      }>
    >({
      queryFn: async (args) => {
        return rtkq(args).exec(() => articlesHttp.search(args));
      },
      providesTags: (result) => [
        { type: 'article', id: 'list' },
        ...(result?.items || []).map(({ id }) => ({
          type: 'article' as const,
          id,
        })),
      ],
    }),

    getArticleDetails: build.query<ArticleState, RtkqRequest<{ id: string }>>({
      queryFn: async (args) => {
        const { id } = args;
        return await rtkq(args).exec(() => articlesHttp.get(id));
      },
      providesTags: (article) => [
        { type: 'article', id: article?.id ?? 'none' },
      ],
    }),

    updateArticle: build.mutation<void, RtkqRequest<UpdateArticleArgs>>({
      queryFn: (args) => {
        return rtkq(args).exec(() => articlesHttp.update(args));
      },
      invalidatesTags: (_, __, args) => [
        { type: 'article', id: 'list' },
        { type: 'article', id: args.id },
      ],
    }),

    addArticle: build.mutation<string, RtkqRequest<AddArticleArgs>>({
      queryFn: (args) => {
        return rtkq(args).exec(() => articlesHttp.create(args));
      },
      invalidatesTags: () => [{ type: 'article', id: 'list' }],
    }),

    removeArticle: build.mutation<void, RtkqRequest<{ id: string }>>({
      queryFn: (args) => {
        return rtkq(args).exec(() => articlesHttp.remove(args.id));
      },
      invalidatesTags: () => [{ type: 'article', id: 'list' }],
    }),

    publishArticle: build.mutation<void, RtkqRequest<{ id: string }>>({
      queryFn: (args) => {
        return rtkq(args).exec(() => articlesHttp.publish(args.id));
      },
      invalidatesTags: (_, __, { id }) => [{ type: 'article', id: id }],
    }),

    hideArticle: build.mutation<void, RtkqRequest<{ id: string }>>({
      queryFn: (args) => {
        return rtkq(args).exec(() => articlesHttp.hide(args.id));
      },
      invalidatesTags: (_, __, { id }) => [{ type: 'article', id: id }],
    }),
  }),
});

export const {
  useLazyGetArticleDetailsQuery,
  useLazyGetArticleListQuery,
  useGetArticleListQuery,
  useGetArticleDetailsQuery,
  useAddArticleMutation,
  useUpdateArticleMutation,
  useRemoveArticleMutation,
  useHideArticleMutation,
  usePublishArticleMutation,
  useGetPublishedArticleListQuery,
  useSearchArticlesQuery,
} = articlesApi;
